import { useCallback, useRef, useState } from 'react'
import { Gauge, X } from 'lucide-react'
import type { EstimationType } from '@/lib/types/estimation'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { ESTIMATION_SCALES, getEstimationDisplayLabel } from '@/lib/types/estimation'

interface EstimationPickerProps {
  value: number | null
  onChange: (value: number | null) => void
  estimationType: EstimationType
  buttonClassName?: string
}

export function EstimationPicker({
  value,
  onChange,
  estimationType,
  buttonClassName,
}: EstimationPickerProps) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const scale = ESTIMATION_SCALES[estimationType]
  const displayLabel = getEstimationDisplayLabel(value, estimationType)

  const handleSelect = useCallback((newValue: number) => {
    onChange(newValue)
    setOpen(false)
  }, [onChange])

  const handleClear = useCallback(() => {
    onChange(null)
    setOpen(false)
  }, [onChange])

  if (estimationType === 'none') return null

  // Freeform: popover with number input
  if (estimationType === 'freeform') {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-2 text-sm',
              buttonClassName,
            )}
          >
            <Gauge className="h-4 w-4 text-muted-foreground" />
            <span>{displayLabel}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-3" align="start">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-medium text-muted-foreground">
              Estimate
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="number"
                min={0}
                max={9999}
                step="any"
                value={value ?? ''}
                placeholder="Enter value"
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:text-sm"
                onChange={(e) => {
                  const val = e.target.value
                  if (val === '') {
                    onChange(null)
                  } else {
                    const num = parseFloat(val)
                    if (!isNaN(num) && num >= 0) {
                      onChange(num)
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setOpen(false)
                  }
                }}
              />
              {value !== null && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Predefined scales: fibonacci, tshirt, exponential
  const scaleValues = scale.values ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 text-sm',
            buttonClassName,
          )}
        >
          <Gauge className="h-4 w-4 text-muted-foreground" />
          <span>{displayLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-1" align="start">
        <div className="flex flex-col">
          <button
            type="button"
            className={cn(
              'flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
              value === null && 'bg-accent',
            )}
            onClick={handleClear}
          >
            <span className="text-muted-foreground">No estimate</span>
          </button>
          {scaleValues.map((scaleValue, index) => {
            const label = scale.displayLabels
              ? scale.displayLabels[index]
              : String(scaleValue)
            const isSelected = value === scaleValue
            return (
              <button
                key={scaleValue}
                type="button"
                className={cn(
                  'flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent',
                  isSelected && 'bg-accent',
                )}
                onClick={() => handleSelect(scaleValue)}
              >
                {label}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
